// import { useQuery as useGraphQLQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ZodSchema } from 'zod'

import Loading from '@/components/loading'
import { useCampaignClient, useDashboardClient } from '@/lib/api/ts-rest-client'
import { CACHE_KEYS } from '@/lib/cache-utils'

type Customer = {
  id: string
  name: string
  brand?: string | null
  integrations?:
  | { id: string; type: string; name: string; features?: any }[]
  | null
}
import { UpdateCampaignDetailsType } from '@/schema/campaign-details.schema'
import { AlphaSmallCalendar } from '../../../components/calendar/small-calendar'
import CampaignButtons from '../../../components/campaign-buttons'
// import CAMPAIGN_GROUPS_QUERY from '../../../graphql/campaign/queries/campaign-groups'

// Reuse existing custom form components with dual-mode support
import { CustomDateComponent } from '@/components/form/custom-date'
import CustomDateTime from '@/components/form/custom-date-time'
import RadioGroupWithDescription from '@/components/form/custom-radio-group-with-description'
import RadioGroupType from '@/components/form/custom-radio-type'
import { CustomSwitchComponent } from '@/components/form/custom-switch'
import CustomTextarea from '@/components/form/custom-textarea'

// Import new form field components
import { FormInput, FormSelect } from '@/components/form-fields'

import styles from '../../../components/form/form.module.css'
import style from '../../campaign/campaign.module.css'

export default function CampaignDetailsForm({
  onSubmit,
  schema,
  values = {} as UpdateCampaignDetailsType,
}: {
  onSubmit: SubmitHandler<UpdateCampaignDetailsType>
  schema: ZodSchema
  values?: UpdateCampaignDetailsType
}) {
  const { t } = useTranslation()

  // Initialize form
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    register,
    formState: { errors },
  } = useForm<UpdateCampaignDetailsType>({
    resolver: zodResolver(schema),
    values,
  })

  // Watch fields for conditional logic
  const typeOfCampaign = watch('typeOfCampaign')
  const customerId = watch('customerId')

  const campaignClient = useCampaignClient()
  const dashboardClient = useDashboardClient()

  // Fetch customers using ts-rest
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: [CACHE_KEYS.CUSTOMERS],
    queryFn: async () => {
      const response = await campaignClient.customers.query()
      return response.status === 200 ? response.body : []
    },
  })

  // Fetch campaign groups using ts-rest
  const { data: campaignGroupsResponse, isLoading: campaignGroupsLoading } =
    useQuery({
      queryKey: [CACHE_KEYS.CAMPAIGN_GROUPS, customerId],
      queryFn: async () => {
        if (!customerId) return null
        const response = await dashboardClient.getCampaignGroups.query({
          query: { customerId },
        })
        return response.status === 200 ? response.body : null
      },
      enabled: !!customerId,
    })

  const campaignGroups = campaignGroupsResponse?.campaignGroups || []

  // Handle scheduleDateTime based on typeOfCampaign
  useEffect(() => {
    if (typeOfCampaign === 'online') {
      const currentValue = watch('scheduleDateTime')
      if (!currentValue) {
        setValue('scheduleDateTime', new Date().toISOString())
      }
    } else {
      setValue('scheduleDateTime', null)
    }
  }, [typeOfCampaign, setValue, watch])

  // Clear campaign group when customer changes
  useEffect(() => {
    setValue('campaignGroupId', '')
  }, [customerId, setValue])

  if (customersLoading || campaignGroupsLoading) return <Loading />

  // Prepare customer options
  const customerOptions =
    customersData?.map((customer: Customer) => ({
      value: customer?.id,
      label: customer?.name,
      data: customer,
    })) || []

  // Prepare campaign group options
  const campaignGroupOptions = customerId
    ? [
      { value: '', label: t('campaign_details.no_group'), data: null },
      ...(campaignGroups?.map((group: any) => ({
        value: group?.id,
        label: group?.name,
        data: group,
      })) || []),
    ]
    : [
      {
        value: '',
        label: t('campaign_details.select_customer_first'),
        data: null,
      },
    ]

  // Campaign category options (for RadioGroupWithDescription)
  const campaignCategoryOptions = [
    {
      name: 'generic',
      label: t('campaign_categories.generic'),
      description: t('campaign_categories.generic_description'),
    },
    {
      name: 'birthday',
      label: t('campaign_categories.birthday'),
      description: t('campaign_categories.birthday_description'),
    },
    {
      name: 'lottery',
      label: t('campaign_categories.lottery'),
      description: t('campaign_categories.lottery_description'),
    },
  ]

  // Create formProps-like object for dual-mode components
  const formProps = {
    control,
    register,
    setValue,
    formValues: values,
    errors,
  }

  return (
    <div className="flex gap-6 w-screen items-start font-inter campaign-details-form">
      <form onSubmit={handleSubmit(onSubmit)} className="flex-1">
        <div className="gap-28 flex">
          {/* Left Column - Form Fields */}
          <div className="basis-1/2 lg:basis-3/5 flex-col">
            {/* Type of Campaign Section */}
            <h1 className="text-xl mb-0">
              {t('campaign_details.type_of_campaign')}
            </h1>

            {/* Customer Select - use FormSelect */}
            <FormSelect
              name="customerId"
              control={control}
              label={t('campaign_details.customer')}
              options={customerOptions}
              placeholder={t('campaign_details.select_customer')}
              testId="customer-select"
              wrapperClassName="mb-4"
            />

            {/* Campaign Group Select - use FormSelect */}
            <FormSelect
              name="campaignGroupId"
              control={control}
              label={t('campaign_details.campaign_group')}
              options={campaignGroupOptions}
              placeholder={
                !customerId
                  ? t('campaign_details.select_customer_first')
                  : t('campaign_details.select_group')
              }
              disabled={!customerId}
              testId="campaign-group-select"
              wrapperClassName="mb-4"
            />

            {/* Type of Campaign Radio - reuse RadioGroupType */}
            <div data-testid="campaign-details-type-of-campaign">
              <RadioGroupType
                field={{
                  name: 'typeOfCampaign',
                  type: 'custom',
                  customControlType: 'radioGroupType',
                  value: '',
                  uiOptions: { testId: 'type-of-campaign' },
                }}
                formProps={formProps as any}
              />
            </div>

            {/* Campaign Categories */}
            <h1 className="text-xl mb-0 mt-4">
              {t('campaign_categories.title')}
            </h1>

            {/* Campaign Category - reuse RadioGroupWithDescription */}
            <div
              className="mb-4"
              data-testid="campaign-details-campaign-category"
            >
              <RadioGroupWithDescription
                field={{
                  name: 'campaignCategory',
                  type: 'custom',
                  customControlType: 'radioGroupWithDescription',
                  value: '',
                  options: campaignCategoryOptions,
                  uiOptions: { testId: 'campaign-category' },
                }}
                formProps={formProps as any}
              />
            </div>

            {/* Referral Campaign */}
            <h2 className="text-md font-bold">
              {t('campaign_details.referral_campaign')}
            </h2>

            {/* Referral Switch - reuse CustomSwitchComponent */}
            <div data-testid="campaign-details-referral">
              <div className="flex flex-row items-center justify-between gap-4 my-2">
                <label className="text-md text-grey-100">
                  {t('campaign_details.include_referral')}
                </label>
                <CustomSwitchComponent
                  field={{
                    name: 'referralCampaign',
                    type: 'custom',
                    customControlType: 'switch',
                    uiOptions: { testId: 'referral-switch' },
                  }}
                  formProps={formProps as any}
                />
              </div>
            </div>

            {/* Campaign Details Section */}
            <h1 data-testid="campaign-details" className="text-xl mt-12 mb-0">
              {t('campaign_details.details')}
            </h1>

            {/* Campaign Name - use FormInput with Controller */}
            <FormInput
              name="name"
              control={control}
              label={t('campaign_details.campaign_name')}
              type="text"
              testId="campaign-details-name"
            />

            {/* Company Name - use FormInput with Controller */}
            <FormInput
              name="companyName"
              control={control}
              label={t('campaign_details.company_name')}
              labelClassName="text-grey-100"
              type="text"
              testId="campaign-details-company-name"
            />

            {/* Dates */}
            <div className="flex flex-col max-w-1/2">
              <div className="flex-1 gap-8 flex">
                {/* Start Date - reuse CustomDateComponent */}
                <div
                  className="flex-1"
                  data-testid="campaign-details-start-date"
                >
                  <label className="text-md text-grey-100">
                    {t('campaign_details.start_date')}
                  </label>
                  <CustomDateComponent
                    name="startDate"
                    control={control}
                    testId="start-date"
                  />
                </div>

                {/* End Date - reuse CustomDateComponent */}
                <div className="flex-1" data-testid="campaign-details-end-date">
                  <label className="text-md text-grey-100">
                    {t('campaign_details.end_date')}
                  </label>
                  <CustomDateComponent
                    name="endDate"
                    control={control}
                    testId="end-date"
                  />
                </div>
              </div>

              {/* Schedule Date Time - Only visible for online campaigns - reuse CustomDateTime */}
              {typeOfCampaign === 'online' && (
                <div
                  className="flex-1 gap-2 flex flex-col min-w-1/2 mt-4"
                  data-testid="campaign-details-schedule-date"
                >
                  <label className="text-md text-grey-100">
                    {t('campaign_details.send_email_date')}
                  </label>
                  <CustomDateTime
                    name="scheduleDateTime"
                    control={control}
                    testId="schedule-date-time"
                  />
                </div>
              )}
            </div>

            {/* Date Validation Error */}
            {errors.dates_error && (
              <div data-testid="dates-error" className="text-primary">
                {t(
                  `validation_messages.${errors.dates_error?.message?.toString()}`,
                )}
              </div>
            )}

            {/* Details Textarea - reuse CustomTextarea */}
            <div className="mb-4" data-testid="campaign-details-details">
              <label className="text-md text-slate-50 font-normal">
                {t('campaign_details.details')}
              </label>
              <CustomTextarea
                field={{
                  name: 'details',
                  type: 'custom',
                  customControlType: 'textarea',
                  uiOptions: { testId: 'details' },
                }}
                formProps={formProps as any}
              />
            </div>

            {/* Sender Address - use FormInput with Controller */}
            <FormInput
              name="senderAddress"
              control={control}
              label={t('campaign_details.sender_address')}
              labelClassName="text-grey-1 font-normal"
              type="text"
              testId="campaign-details-sender-address"
            />

            {/* Email Address - use FormInput with Controller and description */}
            <FormInput
              name="emailAddress"
              control={control}
              label={t('campaign_details.email_address')}
              labelClassName="text-grey-1 font-normal"
              description={t('campaign_details.email_address_label')}
              wrapperClassName={cx(
                'w-full grid space-y-0 mt-2',
                style.senderAddress,
              )}
              type="text"
              testId="campaign-details-email-address"
            />

            {/* Privacy Link - use FormInput with Controller and description */}
            <FormInput
              name="privacyLink"
              control={control}
              label={t('campaign_details.privacy_link')}
              labelClassName="text-grey-1 font-normal"
              description={t('campaign_details.privacy_link_description')}
              wrapperClassName={cx(
                'w-full grid space-y-0 mt-2',
                style.senderAddress,
              )}
              type="text"
              testId="campaign-details-privacy-link"
            />
          </div>

          {/* Right Column - Calendar */}
          <div className="grow-1 basis-1/2 lg:basis-2/5 pr-4">
            <AlphaSmallCalendar
              className={cx('box-content', styles.calendarRatio)}
            />
          </div>
        </div>

        <CampaignButtons />
      </form>
    </div>
  )
}
